import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { priceItems, sizeValid } from "@/lib/pricing";
import { TypeSelect } from "@/components/builder/TypeSelect";
import { UploadBox } from "@/components/builder/UploadBox";
import { SizeFields } from "@/components/builder/SizeFields";
import { ExtrasFields } from "@/components/builder/ExtrasFields";
import { PricePanel } from "@/components/builder/PricePanel";

const Step = ({ n, title, children }) => (
  <section className="panel p-6" data-testid={`builder-step-${n}`}>
    <h2 className="mb-5 flex items-center gap-3 text-lg font-extrabold">
      <span className="grid h-7 w-7 place-items-center rounded bg-gold font-display text-sm text-ink">{n}</span> {title}
    </h2>
    {children}
  </section>
);

export default function OrderBuilder() {
  const { t } = useLang();
  const { items, settings, add } = useStore();
  const nav = useNavigate();
  const [type, setType] = useState("dtf");
  const [upload, setUpload] = useState(null);
  const [width, setWidth] = useState("4");
  const [height, setHeight] = useState("4");
  const [lock, setLock] = useState(true);
  const [qty, setQty] = useState("10");
  const [extras, setExtras] = useState({ rush: false, cut: false, file_check: false });
  const [notes, setNotes] = useState("");

  const ratio = upload ? upload.width_px / upload.height_px : null;
  const w = parseFloat(width);
  const h = parseFloat(height);
  const q = parseInt(qty, 10);
  const valid = sizeValid(type, w, h);
  const item = { print_type: type, width: w, height: h, quantity: q > 0 ? q : 1, ...extras, notes };

  const quote = useMemo(() => {
    if (!settings || !valid) return null;
    return priceItems([...items, item], settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, items, type, w, h, q, extras.rush, extras.cut, extras.file_check, valid]);
  const line = quote?.lines[quote.lines.length - 1];

  const onUploaded = (u) => {
    setUpload(u);
    const r = u.width_px / u.height_px;
    const n = parseFloat(width) || 4;
    setHeight(String(Math.round((n / r) * 100) / 100));
  };

  const canAdd = !!upload && valid && q > 0;
  const hint = !upload ? t("builder.need_file") : null;

  const onAdd = () => {
    add({ ...item, quantity: q, upload: { id: upload.id, original_filename: upload.original_filename, width_px: upload.width_px, height_px: upload.height_px, dpi: upload.dpi }, upload_id: upload.id });
    toast.success(t("builder.added"));
    nav("/cart");
  };

  if (!settings) return <div className="grid min-h-[60vh] place-items-center text-slate-400">{t("common.loading")}</div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="order-builder-page">
      <h1 className="font-display text-4xl sm:text-5xl">{t("builder.title")}</h1>
      <p className="mt-3 max-w-2xl text-slate-400 md:text-lg">{t("builder.sub")}</p>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_380px]">
        <div className="flex flex-col gap-5">
          <Step n={1} title={t("builder.s1")}><TypeSelect value={type} onChange={setType} /></Step>
          <Step n={2} title={t("builder.s2")}><UploadBox upload={upload} onUploaded={onUploaded} width={w} height={h} /></Step>
          <Step n={3} title={t("builder.s3")}>
            <SizeFields type={type} width={width} height={height} onChange={(a, b) => { setWidth(a); setHeight(b); }} lock={lock} setLock={setLock} ratio={ratio} error={!valid && width !== "" && height !== ""} />
          </Step>
          <Step n={4} title={t("builder.s4")}>
            <div className="flex items-center gap-3">
              <input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} className="field w-36 font-mono-num" data-testid="qty-input" />
              <span className="text-sm text-slate-400">{t("builder.pieces")}</span>
            </div>
            <label className="mt-5 block">
              <span className="label">{t("builder.notes")}</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))} rows={3} placeholder={t("builder.notes_ph")} className="field" data-testid="notes-input" />
            </label>
          </Step>
          <Step n={5} title={t("builder.s5")}><ExtrasFields value={extras} onChange={setExtras} settings={settings} /></Step>
        </div>
        <div>
          <PricePanel line={line} quote={quote} type={type} canAdd={canAdd} onAdd={onAdd} hint={hint} />
        </div>
      </div>
    </div>
  );
}
